import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { FinancialModalHost } from "./components/financial/FinancialModalHost";
import Seo from "./components/Seo";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useReducedVisualEffects } from "./hooks/useReducedVisualEffects";

const NotFound = lazy(() => import("@/pages/NotFound"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Groups = lazy(() => import("./pages/Groups"));
const GroupDetailDashboardV2 = lazy(() => import("./pages/GroupDetailDashboardV2"));
const Contributions = lazy(() => import("./pages/Contributions"));
const Comparator = lazy(() => import("./pages/Comparator"));
const Profile = lazy(() => import("./pages/Profile"));
const PublicUserProfile = lazy(() => import("./pages/PublicUserProfile"));
const JoinGroup = lazy(() => import("./pages/JoinGroup"));
const PersonalFinance = lazy(() => import("./pages/PersonalFinance"));
const PersonalFinanceDashboard = lazy(() => import("./pages/PersonalFinanceDashboard"));
const FinancialDashboardV2 = lazy(() => import("./pages/FinancialDashboardV2"));
const FinancialAnalytics = lazy(() => import("./pages/FinancialAnalytics"));
const Investments = lazy(() => import("./pages/Investments"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Cards = lazy(() => import("./pages/Cards"));
const CreditCardInvoiceDetails = lazy(
  () => import("./pages/CreditCardInvoiceDetails")
);
const NetWorth = lazy(() => import("./pages/NetWorth"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Explore = lazy(() => import("./pages/Explore"));
const Ranking = lazy(() => import("./pages/Ranking"));
const Challenges = lazy(() => import("./pages/Challenges"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Settings = lazy(() => import("./pages/Settings"));
const Notifications = lazy(() => import("./pages/Notifications"));
const ActivityCenter = lazy(() => import("./pages/ActivityCenter"));
const FinancialSpaces = lazy(() => import("./pages/FinancialSpaces"));
const FinancialSpaceDetail = lazy(() => import("./pages/FinancialSpaceDetail"));
const FinancialSpaceJoin = lazy(() => import("./pages/FinancialSpaceJoin"));
const Withdrawals = lazy(() => import("./pages/Withdrawals"));
const Goals = lazy(() => import("./pages/Goals"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const FinancialShield = lazy(() => import("./pages/FinancialShield"));
const GamificationNotificationHost = lazy(
  () => import("./components/gamification/GamificationNotificationHost")
);

function Router() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/login" component={Login} />
        <Route path="/update-password" component={UpdatePassword} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/groups" component={Groups} />
        <Route path="/groups/:id" component={GroupDetailDashboardV2} />
        <Route path="/contributions" component={Contributions} />
        <Route path="/comparator" component={Comparator} />
        <Route path="/profile" component={Profile} />
        <Route path="/users/:id" component={PublicUserProfile} />
        <Route path="/join/:token" component={JoinGroup} />
        <Route path="/personal-finance" component={PersonalFinance} />
        <Route path="/personal-finance-dashboard" component={PersonalFinanceDashboard} />
        <Route path="/financial-v2" component={FinancialDashboardV2} />
        <Route path="/analytics" component={FinancialAnalytics} />
        <Route path="/investments" component={Investments} />
        <Route path="/accounts" component={Accounts} />
        <Route
          path="/cards/:cardId/invoice/:invoiceId"
          component={CreditCardInvoiceDetails}
        />
        <Route path="/cards" component={Cards} />
        <Route path="/patrimonio" component={NetWorth} />
        <Route path="/settings" component={Settings} />
        <Route path="/notifications" component={Notifications} />
        <Route path="/activity" component={ActivityCenter} />
        <Route path="/feedback" component={Feedback} />
        <Route path="/explore" component={Explore} />
        <Route path="/ranking" component={Ranking} />
        <Route path="/challenges" component={Challenges} />
        <Route path="/conquistas" component={Achievements} />
        <Route path="/achievements" component={Achievements} />
        <Route path="/financial-spaces" component={FinancialSpaces} />
        <Route path="/financial-spaces/join/:inviteCode" component={FinancialSpaceJoin} />
        <Route path="/financial-spaces/:id" component={FinancialSpaceDetail} />
        <Route path="/goals" component={Goals} />
        <Route path="/withdrawals" component={Withdrawals} />
        <Route path="/blindagem" component={FinancialShield} />
        <Route path="/404" component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function RouteLoading() {
  return (
    <div className="min-h-screen bg-[#03050a] text-white">
      <div className="flex min-h-screen">
        <aside className="hidden w-64 shrink-0 border-r border-white/10 bg-black/25 p-4 lg:block">
          <div className="mb-8 flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-xl bg-gold/30" />
            <div className="space-y-2">
              <div className="h-3 w-28 animate-pulse rounded-full bg-white/15" />
              <div className="h-2 w-20 animate-pulse rounded-full bg-white/10" />
            </div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="h-10 animate-pulse rounded-xl bg-white/[0.045]" />
            ))}
          </div>
        </aside>

        <main className="flex min-w-0 flex-1 items-center justify-center px-4 py-8">
          <div className="w-full max-w-4xl rounded-[2rem] border border-gold/20 bg-white/[0.04] p-5 shadow-[0_0_90px_rgba(245,173,54,0.12)]">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
              <div>
                <p className="text-sm font-bold text-gold">Preparando sua área</p>
                <p className="mt-1 text-xs text-white/45">Carregando os módulos do MetaJuntos.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-28 animate-pulse rounded-2xl border border-white/10 bg-white/[0.035]" />
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function AnalyticsScript() {
  useEffect(() => {
    const endpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
    const websiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
    if (!endpoint || !websiteId) return;
    if (document.querySelector("script[data-metajuntos-analytics='umami']")) return;

    const script = document.createElement("script");
    script.defer = true;
    script.src = `${String(endpoint).replace(/\/$/, "")}/umami`;
    script.dataset.websiteId = websiteId;
    script.dataset.metajuntosAnalytics = "umami";
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

function App() {
  useReducedVisualEffects();

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster
            theme="dark"
            toastOptions={{
              style: {
                background: "oklch(0.12 0.015 260)",
                border: "1px solid oklch(0.78 0.15 75 / 0.2)",
                color: "oklch(0.96 0.005 80)",
              },
            }}
          />
          <AnalyticsScript />
          <Suspense fallback={null}>
            <GamificationNotificationHost />
          </Suspense>
          <Seo />
          <FinancialModalHost />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
